import { SetStateAction, useEffect, useState } from "react";
import { Box, Dialog, DialogContent, IconButton, TextField } from "@material-ui/core";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../Redux/Reducers";
import { addToCart, createOrder, setBillContactModalVisible } from "../../Redux/Actions";
import CloseIcon from "./../../Assets/images/black-close-icon.png";
import CTAButton from "../Common/CTAButton";
import { ToastNotifyError } from "../Toast";
import { useStyles } from "./style";
import EditButton from "../../Assets/images/editButton.png";
import { CURRENCY_SYMBOL } from "../../Config";
import history from "../../Utils/history";
import { getStoreType, isStoreTypeB2b } from "../../Utils/common";
import { ORDER_CUSTOMIZATION, ORDER_TYPE } from "../../Constants/orderStatus";
import { STORE_CUSTOMIZATION } from "../../Constants";

const BillContactModal = (): JSX.Element => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const isB2bStore = isStoreTypeB2b();
  const location: any = history.location;
  const [customerPhone, setCustomerPhone] = useState<string>("");
  const [customerName, setCustomerName] = useState<string>("");
  const [isContactConfirmed, setContactConfirm] = useState<boolean>(false);
  const [isInputAmtVisible, setIsInputAmtVisible] = useState<boolean>(false);

  const { isBillContactModalVisible, cartData, storeData } = useSelector((state: RootState) => ({
    isBillContactModalVisible: state.commonReducer.isBillContactModalVisible,
    cartData: state.cartReducer.data,
    storeData: state.storeReducer.store,
  }));

  useEffect(() => {
    if (location?.state?.order_rfq_details) {
      const { order_rfq_details } = location?.state;
      setCustomerName(order_rfq_details?.name);
      setCustomerPhone(order_rfq_details?.phone);
    }
  }, [location]);

  useEffect(() => {
    if (isBillContactModalVisible) {
      setContactConfirm(false);
    }
  }, [isBillContactModalVisible]);

  const onClose = () => {
    dispatch(setBillContactModalVisible(false));
  };

  const toggleInputVisibility = () => {
    setIsInputAmtVisible(!isInputAmtVisible);
  };

  const onDataChange = (type: any, change: SetStateAction<string>) => {
    switch (type) {
      case "phone":
        if (change.length <= 10) {
          setCustomerPhone(change);
        }
        break;
      case "name":
        setCustomerName(change);
        break;
    }
  };

  const onSendBillBtnClick = () => {
    if (!customerPhone) {
      ToastNotifyError("Phone number field cannot be empty");
    } else {
      // allow click btn event only if phone number length is 10
      if (customerPhone.length === 10) {
        if (!isContactConfirmed) {
          setContactConfirm(!isContactConfirmed);
        } else {
          handleCreateOrder();
        }
      }
    }
  };

  const handleChange = (val: string) => {
    const newState = { ...cartData };
    if (val) {
      if ("pay_amount" in newState) {
        newState.pay_amount = parseFloat(val).toFixed(2);
      } else {
        newState.amount = parseFloat(val).toFixed(2);
      }
      dispatch(addToCart(newState));
    } else {
      if ("pay_amount" in newState) {
        newState.pay_amount = 0;
      } else {
        newState.amount = 0;
      }
      dispatch(addToCart(newState));
    }
  };

  const parseCartItems = (tempData: {
    items: { item_type: string; item_price: any; item_quantity: number; discounted_price: number }[];
  }) => {
    const data: any[] = [];
    tempData.items.map(
      (item: { item_type: string; item_price: any; item_quantity: number; discounted_price: number }) => {
        const newItem = {
          ...item,
          actual_price: item.item_price,
          amount: item.item_quantity * item.discounted_price,
          quantity: Number(item.item_quantity),
          item_quantity: String(item.item_quantity),
        };
        data.push(newItem);
      },
    );
    return data;
  };

  const handleCreateOrder = () => {
    const parsedCartItems = parseCartItems(cartData);
    const newCartData = {
      delivery_charge: null,
      extra_charge_name: null,
      extra_charges: null,
      discount: null,
      ...cartData,
      items: parsedCartItems,
      store_id: storeData.store_id,
      name: customerName,
      phone: customerPhone,
      order_id: location?.state?.order_id,
      order_type: location?.state?.order_id ? ORDER_TYPE.B2B : ORDER_TYPE.SELF_BILLED,
      order_mode: isB2bStore
        ? ORDER_CUSTOMIZATION.B2B
        : getStoreType() === STORE_CUSTOMIZATION.ORDER_BASED
          ? ORDER_CUSTOMIZATION.ORDER
          : 1,
    };
    setCustomerPhone("");
    setCustomerName("");
    dispatch(setBillContactModalVisible(false));
    dispatch(createOrder(newCartData));
  };

  return (
    <Dialog open={isBillContactModalVisible} maxWidth="xs" fullWidth={true} onClose={onClose} className="relative">
      <IconButton
        color="primary"
        aria-label="Close Order Details Pop Up"
        component="span"
        className={classes.closeIcon}
        onClick={onClose}
      >
        <img src={CloseIcon} alt="close icon" loading="lazy" className="img h2" />
      </IconButton>
      <DialogContent>
        {!isContactConfirmed ? 
          <>
            <Box component="h1" className="mv4">
              Enter Phone Number & Name
            </Box>
            <Box component="section" className="mb4">
              <TextField
                className="w-100"
                value={customerPhone}
                variant="outlined"
                placeholder="Enter Customer Phone Number"
                onChange={(e) => onDataChange("phone", e.target.value.replace(/[^0-9]/g, ""))}
              />
            </Box>
            <Box component="section" className="mb4">
              <TextField
                className="w-100"
                value={customerName}
                variant="outlined"
                placeholder="Enter Customer Name(Optional)"
                onChange={(e) => onDataChange("name", e.target.value)}
              />
            </Box>
          </>
         : 
          <>
            <Box component="h1" className="mv4">
              Please Confirm
            </Box>
            <Box marginBottom="30px">
              <Box className="mb3 center ph3" maxWidth="300">
                <Box component="p" className="f5 gray mt0 mb2 tc">
                  Customer WhatsApp Number
                </Box>
                <Box className="relative" display="flex" flexDirection="column" alignItems="center">
                  <Box component="p" className={`mv0 f5`}>
                    {customerName}
                  </Box>
                  <Box component="p" className={`mv0 ${customerName !== "" ? `f5` : `f3`}`}>
                    {customerPhone}
                  </Box>
                  <Box
                    className="absolute pointer"
                    top="50%"
                    right="0"
                    style={{
                      transform: "translateY(-50%)",
                    }}
                  >
                    <img src={EditButton} width="30" onClick={() => setContactConfirm(false)} />
                  </Box>
                </Box>
              </Box>
              <Box className="mb3 center ph3" maxWidth="300">
                <Box component="p" className="f5 gray mt0 mb2 tc">
                  Bill Amount
                </Box>
                <Box className="relative" display="flex" flexDirection="column" alignItems="center">
                  {isInputAmtVisible ? 
                    <Box className="center mw5 relative">
                      <Box
                        component="p"
                        className="mv0 absolute left-1"
                        style={{
                          top: "50%",
                          transform: "translateY(-50%)",
                        }}
                      >
                        {CURRENCY_SYMBOL}
                      </Box>
                      <input
                        className="pl4 pv2 pr2 w-100 f5"
                        value={
                          cartData.pay_amount || cartData.pay_amount === 0
                            ? parseFloat(cartData.pay_amount.toFixed(2))
                            : cartData.amount
                              ? cartData.amount
                              : ""
                        }
                        placeholder="Item Name & Size"
                        onChange={(e) => handleChange(e.target.value)}
                      />
                    </Box>
                   : 
                    <Box component="p" className={`mv0 f3`}>
                      {CURRENCY_SYMBOL}
                      {cartData.pay_amount || cartData.pay_amount === 0
                        ? parseFloat(cartData.pay_amount.toFixed(2))
                        : cartData.amount
                          ? cartData.amount
                          : ""}
                    </Box>
                  }

                  {/* <Box
                    className="absolute pointer"
                    top="50%"
                    right="0"
                    style={{
                      transform: "translateY(-50%)",
                    }}
                  >
                    <img src={EditButton} width="30" onClick={toggleInputVisibility} />
                  </Box> */}
                </Box>
              </Box>
            </Box>
          </>
        }
        <Box className="mv3">
          <CTAButton
            customStyles={{ opacity: customerPhone.length === 10 ? 1 : 0.5 }}
            customClasses="br-pill w-100 flex justify-center pa3"
            text="SEND BILL"
            onClick={onSendBillBtnClick}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BillContactModal;
